/** Chatroom DO fetch-route table SSOT: the path/method/handler table the conversation DO dispatches incoming requests against. Pure-leaf module, so no cycle through the barrel. */

import { HTTP_METHODS } from '@federa/const/http-methods';
import type { ChatRoomRef } from '@federa/types/adapters-chat-session';

/** Path segment of each chatroom DO fetch route, keyed so a CALLER can name it: `CHATROOM_FETCH_ROUTES` is read by iteration, so clients re-typed the segment -- a witnessed 404. */
export const CHATROOM_FETCH_PATHS = {
	WEBSOCKET: '/websocket',
	BROADCAST: '/broadcast',
	WEBHOOK_MESSAGE: '/webhook-message',
	PARTICIPANTS: '/participants',
	LOAD_PREVIOUS_MESSAGES: '/load-previous-messages',
	CALL_STORAGE_AGENTS: '/call-storage-agents',
} as const;

/**
 * One chatroom DO fetch route.
 *
 * `handler` is `keyof ChatRoomRef`, NOT `string`: the router resolves it by dynamic lookup, so a `string` column let a
 * renamed DO method compile clean and 404 at run time on every request to that path.
 *
 * `clientForwardable` says whether the chats ENTRY may forward a browser request onto this DO path
 * (`forwardToRoomObject`). It is a SECURITY column, not a convenience one: `/broadcast` and `/webhook-message` take the
 * sender identity off the request BODY, so a client that reaches them can publish and persist a frame under any other
 * participant's name. Their real callers -- `flows/broadcast-message-do.ts` and `utils/assistant/agent-run-broadcast.ts`
 * -- dial the DO over the `rooms` NAMESPACE BINDING and never cross the entry, so denying the forward removes the
 * forgery surface entirely without touching them.
 */
export interface ChatroomFetchRoute {
	path: string;
	method?: string;
	handler: keyof ChatRoomRef;
	passRequest: boolean;
	allowFallthrough?: boolean;
	clientForwardable: boolean;
}

export const CHATROOM_FETCH_ROUTES: ChatroomFetchRoute[] = [
	{ path: CHATROOM_FETCH_PATHS.WEBSOCKET, handler: 'handleWebSocket', passRequest: true, clientForwardable: true },
	{
		path: CHATROOM_FETCH_PATHS.BROADCAST,
		method: HTTP_METHODS.POST,
		handler: 'handleBroadcast',
		passRequest: true,
		// Binding-only: the payload names its own sender, so a client-reachable /broadcast forges another participant.
		clientForwardable: false,
	},
	{
		path: CHATROOM_FETCH_PATHS.WEBHOOK_MESSAGE,
		method: HTTP_METHODS.POST,
		handler: 'handleWebhookMessage',
		passRequest: true,
		// Binding-only for the same reason: `name`/`user_id`/`message_id` are read straight off the body and persisted.
		clientForwardable: false,
	},
	// passRequest: the handler reads no body, but it needs the serving request to continue an inbound W3C trace -- a DO
	// method has no other way to see the request that provoked it, and a DO-held carrier would cross-attribute peers.
	{ path: CHATROOM_FETCH_PATHS.PARTICIPANTS, handler: 'handleParticipants', passRequest: true, clientForwardable: true },
	{
		path: CHATROOM_FETCH_PATHS.LOAD_PREVIOUS_MESSAGES,
		// DECLARED, like its three siblings. Without it a GET reached `handleLoadPreviousMessages`, whose method guard
		// answered `null`, and the router -- no `allowFallthrough` on this row -- returned that null as the DO response.
		// The runtime rejects a null from a stub fetch, so the caller saw an opaque 500 instead of a 404.
		method: HTTP_METHODS.POST,
		handler: 'handleLoadPreviousMessages',
		passRequest: true,
		clientForwardable: true,
	},
	{
		path: CHATROOM_FETCH_PATHS.CALL_STORAGE_AGENTS,
		method: HTTP_METHODS.POST,
		handler: 'handleCallStorageAgents',
		passRequest: true,
		// The operator desk tool dials this over HTTP; the route carries its own constant-time DESK_CALLER_SECRET gate.
		clientForwardable: true,
	},
];

/** The DO paths the chats entry may forward a client request onto -- derived from the table above so the allowlist and
 *  the route definitions cannot drift into a re-opened forgery surface. */
export const CHATROOM_CLIENT_FORWARDABLE_PATHS: ReadonlySet<string> = new Set(
	CHATROOM_FETCH_ROUTES.filter((route) => route.clientForwardable).map((route) => route.path),
);
